/**
 * Détail d'un mois de trésorerie
 * Affiche les encaissements et décaissements avec récapitulatif
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  AlertCircle,
  Building2,
  FileText,
  Home,
} from 'lucide-react';
import type { MoisTresorerie, FluxTresorerie, CategorieFlux, ModeAffichageTresorerie } from '@/types/tresorerie';
import { CATEGORIES_LABELS, CATEGORIES_COLORS } from '@/types/tresorerie';

interface TresorerieMoisDetailProps {
  mois: MoisTresorerie;
  mode?: ModeAffichageTresorerie;
}

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Retourne le label d'une catégorie
 */
function getCategorieLabel(categorie: string): string {
  return CATEGORIES_LABELS[categorie as CategorieFlux] || categorie;
}

/**
 * Composant pour afficher un flux standard
 */
function FluxItem({
  flux,
  isEncaissement,
  mode = 'ttc',
}: {
  flux: FluxTresorerie;
  isEncaissement: boolean;
  mode?: ModeAffichageTresorerie;
}) {
  const colorClass = isEncaissement ? 'text-green-500' : 'text-red-500';
  const bgColor = CATEGORIES_COLORS[flux.categorie as CategorieFlux] || '#6b7280';
  const montant = mode === 'ht' ? flux.montant_ht : flux.montant;

  return (
    <div className="flex items-start justify-between border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{flux.libelle}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: bgColor, color: bgColor }}
          >
            {getCategorieLabel(flux.categorie)}
          </Badge>
          {flux.chantier_nom && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {flux.chantier_nom}
            </span>
          )}
        </div>
      </div>
      <div className="text-right ml-4 flex-shrink-0">
        <p className={cn('font-medium', colorClass)}>
          {isEncaissement ? '+' : '-'}
          {formatCurrency(montant)}
        </p>
        <p className="text-xs text-muted-foreground">
          {format(new Date(flux.date_prevue), 'dd MMM', { locale: fr })}
        </p>
        {flux.statut === 'en_retard' && (
          <Badge variant="destructive" className="text-xs mt-1">
            <AlertCircle className="h-3 w-3 mr-1" />
            En retard
          </Badge>
        )}
        {flux.statut === 'realise' && (
          <Badge variant="secondary" className="text-xs mt-1">
            Réalisé
          </Badge>
        )}
      </div>
    </div>
  );
}

/**
 * Composant pour afficher une charge fixe (avec jour de prélèvement)
 */
function ChargeFixeItem({ flux, mode = 'ttc' }: { flux: FluxTresorerie; mode?: ModeAffichageTresorerie }) {
  const bgColor = CATEGORIES_COLORS[flux.categorie as CategorieFlux] || '#94a3b8';
  const jourPrelevement = flux.date_prevue ? parseInt(flux.date_prevue.split('-')[2], 10) : null;
  const montant = mode === 'ht' ? flux.montant_ht : flux.montant;

  return (
    <div className="flex items-start justify-between border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{flux.libelle}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: bgColor, color: bgColor }}
          >
            Charge fixe
          </Badge>
        </div>
      </div>
      <div className="text-right ml-4 flex-shrink-0">
        <p className="font-medium text-red-500">
          -{formatCurrency(montant)}
        </p>
        {jourPrelevement && (
          <p className="text-xs text-muted-foreground">
            le {jourPrelevement}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Composant détail du mois
 */
export function TresorerieMoisDetail({ mois, mode = 'ttc' }: TresorerieMoisDetailProps) {
  const isHt = mode === 'ht';
  const totalEnc = isHt ? mois.total_encaissements_ht : mois.total_encaissements;
  const totalDec = isHt ? mois.total_decaissements_ht : mois.total_decaissements;
  const variation = totalEnc - totalDec;
  const soldeDebut = isHt ? mois.solde_debut_ht : mois.solde_debut;
  const soldeFin = isHt ? mois.solde_fin_ht : mois.solde_fin;
  const detailEnc = isHt ? mois.detail_encaissements_ht : mois.detail_encaissements;
  const detailDec = isHt ? mois.detail_decaissements_ht : mois.detail_decaissements;
  const getMontant = (flux: FluxTresorerie) => isHt ? flux.montant_ht : flux.montant;

  // Séparer les décaissements en 2 groupes : chantiers et charges fixes
  const decaissementsChantiers = mois.decaissements.filter(d => d.categorie !== 'charges_fixes');
  const chargesFixes = mois.decaissements.filter(d => d.categorie === 'charges_fixes');
  const totalChantiers = decaissementsChantiers.reduce((sum, d) => sum + getMontant(d), 0);
  const totalChargesFixes = chargesFixes.reduce((sum, d) => sum + getMontant(d), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
      {/* Encaissements */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowDownCircle className="h-5 w-5 text-green-500" />
            <h3 className="font-semibold">Encaissements prévus</h3>
            <span className="ml-auto text-green-500 font-bold">
              +{formatCurrency(totalEnc)}
            </span>
          </div>

          {mois.encaissements.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">
              Aucun encaissement prévu ce mois
            </p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {mois.encaissements.map((flux) => (
                <FluxItem key={flux.id} flux={flux} isEncaissement={true} mode={mode} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Décaissements */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <ArrowUpCircle className="h-5 w-5 text-red-500" />
            <h3 className="font-semibold">Décaissements prévus</h3>
            <span className="ml-auto text-red-500 font-bold">
              -{formatCurrency(totalDec)}
            </span>
          </div>

          {mois.decaissements.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">
              Aucun décaissement prévu ce mois
            </p>
          ) : (
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {/* Sous-section : Chantiers */}
              {decaissementsChantiers.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Chantiers</span>
                    <span className="ml-auto text-sm text-red-500 font-medium">
                      -{formatCurrency(totalChantiers)}
                    </span>
                  </div>
                  <div className="space-y-3 pl-2">
                    {decaissementsChantiers.map((flux) => (
                      <FluxItem key={flux.id} flux={flux} isEncaissement={false} mode={mode} />
                    ))}
                  </div>
                </div>
              )}

              {/* Sous-section : Charges fixes */}
              {chargesFixes.length > 0 && (
                <div className={decaissementsChantiers.length > 0 ? 'mt-4 pt-4 border-t border-border/50' : ''}>
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Charges fixes</span>
                    <span className="ml-auto text-sm text-red-500 font-medium">
                      -{formatCurrency(totalChargesFixes)}
                    </span>
                  </div>
                  <div className="space-y-3 pl-2">
                    {chargesFixes.map((flux) => (
                      <ChargeFixeItem key={flux.id} flux={flux} mode={mode} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résumé du mois */}
      <Card className="lg:col-span-2">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Solde début de mois</p>
              <p className="text-lg font-medium">{formatCurrency(soldeDebut)}</p>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">Variation</p>
              <p
                className={cn(
                  'text-lg font-medium',
                  variation >= 0 ? 'text-green-500' : 'text-red-500'
                )}
              >
                {variation >= 0 ? '+' : ''}
                {formatCurrency(variation)}
              </p>
            </div>

            <div className="text-right">
              <p className="text-sm text-muted-foreground">Solde fin de mois</p>
              <p
                className={cn(
                  'text-xl font-bold',
                  soldeFin >= 0 ? 'text-green-500' : 'text-red-500'
                )}
              >
                {formatCurrency(soldeFin)}
              </p>
              {soldeFin < 0 && (
                <Badge variant="destructive" className="mt-1">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Déficit
                </Badge>
              )}
            </div>
          </div>

          {/* Détail par catégorie si plusieurs flux */}
          {(mois.encaissements.length > 3 || mois.decaissements.length > 3) && (
            <div className="mt-6 pt-4 border-t">
              <p className="text-sm font-medium mb-3">Répartition par catégorie</p>
              <div className="grid grid-cols-2 gap-4">
                {/* Encaissements par catégorie */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Encaissements</p>
                  <div className="space-y-1">
                    {Object.entries(detailEnc).map(([cat, montant]) => (
                      <div key={cat} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {getCategorieLabel(cat)}
                        </span>
                        <span className="text-green-500">
                          +{formatCurrency(montant as number)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Décaissements par catégorie */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Décaissements</p>
                  <div className="space-y-1">
                    {Object.entries(detailDec).map(([cat, montant]) => (
                      <div key={cat} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {getCategorieLabel(cat)}
                        </span>
                        <span className="text-red-500">
                          -{formatCurrency(montant as number)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
